// ============================================================================
// TrimaInvitation — Entry screen for the Trima parcours
// ============================================================================
// Rendered in two contexts:
//   1. As a slide at the end of the ComColors questionnaire (TRIMA_INVITATION
//      type in Questionnaire.tsx). User clicks "Commencer" → navigate to
//      /questionnaire-competences.
//   2. As the first screen of the Trima page itself (when user lands via the
//      direct route, or after refusing once and coming back).
//
// "Pas pour moi" → cleanup any persisted state, redirect to home.
// ============================================================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Button from 'components/common/button/Button';
import { ROUTES } from 'constants/routes';
import { trimaStorage } from 'helpers/trima/trimaStorage';
import { TrimaInvitationWrapper } from 'pages/questionnaire-trima/slides/invitation/TrimaInvitation.styled';

export interface TrimaInvitationProps {
  /**
   * Called when the user clicks "Commencer".
   * If omitted, defaults to navigating to the Trima page.
   * Override this when used as a slide in the ComColors Swiper, to control
   * navigation explicitly.
   */
  onAccept?: () => void;
  /**
   * Called when the user clicks "Pas pour moi".
   * If omitted, defaults to clearing state and navigating to home.
   */
  onRefuse?: () => void;
}

const TrimaIllustration: React.FC = () => (
  // Placeholder 4-branch star evoking the 4 cadrans.
  // Replace with a curated SVG from assets/svg if/when the design team supplies one.
  <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M50 5 L58 42 L95 50 L58 58 L50 95 L42 58 L5 50 L42 42 Z"
      fill="currentColor"
      opacity="0.85"
    />
    <circle cx="50" cy="50" r="6" fill="white" />
  </svg>
);

const TrimaInvitation: React.FC<TrimaInvitationProps> = ({ onAccept, onRefuse }) => {
  const { t } = useTranslation('trima');
  const history = useHistory();

  const handleAccept = () => {
    if (onAccept) {
      onAccept();
    } else {
      history.push(ROUTES.TRIMA);
    }
  };

  const handleRefuse = () => {
    if (onRefuse) {
      onRefuse();
    } else {
      trimaStorage.clear();
      history.push(ROUTES.INDEX);
    }
  };

  return (
    <TrimaInvitationWrapper>
      <button
        className="trima-close-btn"
        onClick={handleRefuse}
        aria-label={t('invitation.cta-refuse')}
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      <div className="trima-illustration">
        <TrimaIllustration />
      </div>

      <h1>{t('invitation.title')}</h1>

      <p className="trima-subtitle">{t('invitation.subtitle')}</p>

      <div className="trima-duration">⏱ {t('invitation.duration')}</div>

      <p className="trima-warning">{t('invitation.warning')}</p>

      <div className="trima-buttons">
        <Button fill="solid" color='accent' expand onClick={handleAccept}>
          <span>{t('invitation.cta-start')}</span>
        </Button>
      </div>
    </TrimaInvitationWrapper>
  );
};

export default TrimaInvitation;
